<template>
  <div class="calendar-gen-debug">
    <ul class="calendar-gen-debug__list" v-if="props.genMetaInfo">
      <li class="calendar-gen-debug__item">
        <div class="calendar-gen-debug__left">
          <div class="calendar-gen-debug__label">Source:</div>
          <div class="calendar-gen-debug__value">
            <b>{{ props.genMetaInfo.sourceType || "N/A" }}</b>

            <InfoIconWithTooltip v-if="props.genMetaInfo.locationExternalId" :width="350" :iconSize="12" style="margin-left: 4px">
              {{ props.genMetaInfo.locationExternalId }}
            </InfoIconWithTooltip>
          </div>
        </div>
      </li>

      <li class="calendar-gen-debug__item">
        <div class="calendar-gen-debug__left">
          <div class="calendar-gen-debug__label">ContentFit:</div>
          <div class="calendar-gen-debug__value" :class="getContentFitClass(props.genMetaInfo.contentFit)">
            {{ props.genMetaInfo.contentFit || "N/A" }}
          </div>
        </div>
      </li>

      <li class="calendar-gen-debug__item">
        <div class="calendar-gen-debug__left">
          <div class="calendar-gen-debug__label">AffinityScore:</div>
          <div class="calendar-gen-debug__value" :class="getAffinityScoreClass(props.genMetaInfo.affinityScore)">
            {{ props.genMetaInfo.affinityScore != null ? props.genMetaInfo.affinityScore.toFixed(2) : "N/A" }}
          </div>
        </div>
      </li>

      <!--<li class="calendar-gen-debug__item">
        <div class="calendar-gen-debug__left">
          <div class="calendar-gen-debug__label">Found Content:</div>
          <div class="calendar-gen-debug__value">
            <IconEmbedded v-if="props.genMetaInfo.foundContent" name="check_4-5" :size="18" color="rgba(61, 133, 40, 1)" />
            <span v-else class="calendar-gen-debug__fail-icon">
              <IconEmbedded name="remove_4" :size="16" color="rgba(173, 45, 45, 1)" />
            </span>
          </div>
        </div>
      </li>-->

      <li v-if="props.genMetaInfo.rationale" class="calendar-gen-debug__item calendar-gen-debug__item--full-width">
        <div class="calendar-gen-debug__left">
          <div class="calendar-gen-debug__value calendar-gen-debug__value--multiline">{{ props.genMetaInfo.rationale }}</div>
        </div>
      </li>

      <!--<li v-if="props.genMetaInfo.candidateContentSummaryIds?.length" class="calendar-gen-debug__item calendar-gen-debug__item--full-width">
        <div class="calendar-gen-debug__left">
          <div class="calendar-gen-debug__label">Candidate Content IDs ({{ props.genMetaInfo.candidateContentSummaryIds.length }}):</div>
          <div class="calendar-gen-debug__value calendar-gen-debug__value--multiline">
            <div v-for="(id, index) in props.genMetaInfo.candidateContentSummaryIds" :key="id" class="calendar-gen-debug__candidate-id">{{ index + 1 }}. {{ id }}</div>
          </div>
        </div>
      </li>-->
    </ul>
    <div v-else class="calendar-gen-debug__no-data">No generation metadata available</div>
  </div>
</template>

<script setup lang="ts">
// Components
import InfoIconWithTooltip from "@components/ui/InfoIconWithTooltip.vue";

// Types
import { GenMetaInfo } from "@contracts/contentCalendar";

const props = defineProps<{
  genMetaInfo: GenMetaInfo | undefined | null;
}>();

function getContentFitClass(contentFit: string | undefined): string {
  if (!contentFit) return "";
  const fit = contentFit.toLowerCase();
  if (fit === "strong") return "calendar-gen-debug__value--excellent";
  if (fit === "good" || fit === "moderate") return "calendar-gen-debug__value--good";
  if (fit === "weak") return "calendar-gen-debug__value--poor";
  return "";
}

function getAffinityScoreClass(score: number | undefined): string {
  if (score == null) return "";
  if (score >= 0.7) return "calendar-gen-debug__value--excellent";
  if (score >= 0.5) return "calendar-gen-debug__value--good";
  if (score >= 0.3) return "calendar-gen-debug__value--ok";
  return "calendar-gen-debug__value--poor";
}
</script>

<style scoped lang="scss">
.calendar-gen-debug {
  margin: 12px 0 0;
  padding: 10px;
  border: 1px rgba(0, 0, 0, 0.1) solid;
  border-radius: 6px;
  background: #fafafa;

  &__header {
    margin-bottom: 8px;
    font-weight: 700;
    font-size: 13px;
    color: rgba(0, 0, 0, 0.7);
  }

  &__list {
    padding: 0;
    margin: 0;
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  &__item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 6px 8px;
    border: 1px rgba(0, 0, 0, 0.08) solid;
    border-radius: 6px;
    background: white;

    &--full-width {
      .calendar-gen-debug__left {
        flex-direction: column;
        align-items: flex-start;
        width: 100%;
      }
    }
  }

  &__left {
    display: flex;
    align-items: center;
    gap: 10px;
    flex: 1;
  }

  &__label {
    font-weight: 600;
    font-size: 12px;
  }

  &__value {
    font-size: 12px;
    color: rgba(0, 0, 0, 0.8);

    &--multiline {
      margin-top: 4px;
      white-space: pre-wrap;
      word-break: break-word;
    }

    &--excellent {
      color: rgba(36, 137, 67, 1);
      font-weight: 600;
    }
    &--good {
      color: rgba(66, 160, 73, 1);
      font-weight: 600;
    }
    &--ok {
      color: rgba(219, 160, 29, 1);
      font-weight: 600;
    }
    &--poor {
      color: rgba(173, 45, 45, 1);
      font-weight: 600;
    }
  }

  &__candidate-id {
    padding: 2px 0;
    font-size: 11px;
    font-family: monospace;
    color: rgba(0, 0, 0, 0.7);
  }

  &__fail-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  &__no-data {
    padding: 8px;
    color: rgba(0, 0, 0, 0.5);
    font-size: 12px;
    font-style: italic;
  }
}
</style>
